import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

export default function ConfigPage() {
    const current = useQuery(api.index.settings_getForCurrentTenant) as
        | {
              slotIncrementMinutes: number;
              appointmentDurationMinutes: number;
              customSystemPrompt?: string;
              shopName?: string;
              shopAddress?: string;
          }
        | null
        | undefined;
    const save = useMutation(api.index.settings_upsertForCurrentTenant);

    const [slotInc, setSlotInc] = useState(15);
    const [duration, setDuration] = useState(30);
    const [prompt, setPrompt] = useState("");
    const [shopName, setShopName] = useState("");
    const [shopAddress, setShopAddress] = useState("");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!current) return;
        setSlotInc(current.slotIncrementMinutes ?? 15);
        setDuration(current.appointmentDurationMinutes ?? 30);
        setPrompt(current.customSystemPrompt ?? "");
        setShopName(current.shopName ?? "");
        setShopAddress(current.shopAddress ?? "");
    }, [
        current?.slotIncrementMinutes,
        current?.appointmentDurationMinutes,
        current?.customSystemPrompt,
        current?.shopName,
        current?.shopAddress,
    ]);

    return (
        <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Configurações da barbearia
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Defina como o agendamento funciona e personalize o
                    comportamento do assistente.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Nome da barbearia
                        </label>
                        <input
                            value={shopName}
                            onChange={(e) => setShopName(e.target.value)}
                            placeholder="Ex.: BarberBot Studio"
                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Endereço
                        </label>
                        <input
                            value={shopAddress}
                            onChange={(e) => setShopAddress(e.target.value)}
                            placeholder="Rua Exemplo, 123 - Bairro, Cidade"
                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Incremento de horário (minutos)
                        </label>
                        <input
                            type="number"
                            min={1}
                            step={1}
                            value={slotInc}
                            onChange={(e) =>
                                setSlotInc(Number(e.target.value) || 1)
                            }
                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Define o espaçamento da grade de horários. Ex.: 15
                            cria horários como 10:00, 10:15, 10:30.
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Duração padrão do atendimento (minutos)
                        </label>
                        <input
                            type="number"
                            min={1}
                            step={1}
                            value={duration}
                            onChange={(e) =>
                                setDuration(Number(e.target.value) || 1)
                            }
                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Tempo ocupado por cada agendamento. Ex.: 30 bloqueia
                            30 minutos por cliente.
                        </p>
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Prompt personalizado do assistente (opcional)
                    </label>
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        rows={6}
                        placeholder="Instruções adicionais para o comportamento do BarberBot"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        O texto será adicionado ao prompt do sistema do
                        BarberBot em todas as conversas.
                    </p>
                </div>

                <div className="mt-4 flex items-center gap-3">
                    <button
                        disabled={saving}
                        onClick={async () => {
                            setSaving(true);
                            setSaved(false);
                            try {
                                await save({
                                    slotIncrementMinutes: Math.max(
                                        1,
                                        Math.floor(slotInc),
                                    ),
                                    appointmentDurationMinutes: Math.max(
                                        1,
                                        Math.floor(duration),
                                    ),
                                    customSystemPrompt:
                                        prompt.trim() || undefined,
                                    shopName: shopName.trim() || undefined,
                                    shopAddress:
                                        shopAddress.trim() || undefined,
                                } as any);
                                setSaved(true);
                            } finally {
                                setSaving(false);
                            }
                        }}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors"
                    >
                        {saving ? "Salvando..." : "Salvar configurações"}
                    </button>
                    {saved && (
                        <span className="text-sm text-emerald-700 dark:text-emerald-400">
                            Configurações salvas!
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
